import { Controller } from '../utils/controller';
import Swiper, { Autoplay, Navigation, Pagination, Thumbs, Lazy } from 'swiper';

Swiper.use([Lazy]);

export default class extends Controller {
  static targets = [
    'thumbnail',
    'pagination',
    'thumbnailLeftNav',
    'thumbnailRightNav',
    'leftNav',
    'rightNav',
    'slide'
  ];
  static requiredTargets = [];

  static values = {
    slidesId: String,
    slidesPerViewThumbnail: { type: Number, default: 5 },
    slidesSpaceBetweenThumbnail: { type: Number, default: 10 },
    slidesAutoHeight: { type: Boolean, default: false },
    slidesAutoPlay: { type: Boolean, default: false },
    slidesPerViewXxl: { type: Number, default: 4 },
    slidesPerViewXl: { type: Number, default: 4 },
    slidesPerViewL: { type: Number, default: 3 },
    slidesPerViewM: { type: Number, default: 2 },
    slidesPerViewS: { type: Number, default: 1 },
    slidesPerViewXs: { type: Number, default: 1 },
    slidesPerGroupXl: { type: Number, default: 1 },
    slidesPerGroupL: { type: Number, default: 1 },
    slidesPerGroupM: { type: Number, default: 1 },
    slidesPerGroupS: { type: Number, default: 1 },
    slidesSpaceBetween: { type: Number, default: 10 },
    slidesLoop: { type: Boolean, default: false },
    slidesLoopDesktop: { type: String, default: 'default' },
    thumbsSlidesPerGroup: { type: Number, default: 1 },
    thumbsTranslate: { type: Number, default: 0 },
    // NOTE: When the transition to `proper_components` is complete, this can be
    // removed.
    slidesLcId: { type: String, default: '' },
    slidesTrackActive: { type: Boolean, default: false },
    slidesTrackActiveTarget: { type: String, default: '' },
    slidesStartAt: { type: Number, default: 0 },
    slidesSyncCarouselId: String
  };

  // TARGETS
  declare thumbnailTarget: HTMLElement;
  declare thumbnailLeftNavTarget: HTMLElement;
  declare thumbnailRightNavTarget: HTMLElement;
  declare paginationTarget: HTMLElement;
  declare leftNavTarget: HTMLElement;
  declare rightNavTarget: HTMLElement;
  declare slideTarget: HTMLElement;
  declare readonly hasThumbnailTarget: boolean;
  declare readonly hasThumbnailLeftNavTarget: boolean;
  declare readonly hasThumbnailRightNavTarget: boolean;
  declare readonly hasPaginationTarget: boolean;
  declare readonly hasLeftNavTarget: boolean;
  declare readonly hasRightNavTarget: boolean;
  declare readonly hasSlideTarget: boolean;

  // VALUES
  declare slidesPerViewThumbnailValue: number;
  declare slidesSpaceBetweenThumbnailValue: number;
  declare slidesAutoHeightValue: boolean;
  declare slidesAutoPlayValue: boolean;
  declare slidesIdValue: string;
  declare slidesPerViewXxlValue: number;
  declare slidesPerViewXlValue: number;
  declare slidesPerViewLValue: number;
  declare slidesPerViewMValue: number;
  declare slidesPerViewSValue: number;
  declare slidesPerViewXsValue: number;
  declare slidesPerGroupXlValue: number;
  declare slidesPerGroupLValue: number;
  declare slidesPerGroupMValue: number;
  declare slidesPerGroupSValue: number;
  declare slidesSpaceBetweenValue: number;
  declare slidesLoopValue: boolean;
  declare slidesLoopDesktopValue: string;
  declare slidesLcId: string;
  declare slidesTrackActiveValue: boolean;
  declare slidesTrackActiveTargetValue: string;
  declare slidesStartAtValue: number;
  declare thumbsSlidesPerGroupValue: number;
  declare thumbsTranslateValue: number;
  declare readonly hasThumbsTranslateValue: boolean;

  // LOCAL STATE
  private slide: Swiper;
  private resizeObserver;
  private intersectionObserver;

  thumbnails() {
    // short circuit the thumbs config
    if (!this.hasThumbnailTarget) {
      return {};
    }

    const thumbsSlider = new Swiper(this.thumbnailTarget, {
      modules: [Navigation],
      slidesPerView: 'auto',
      slidesPerGroup: this.thumbsSlidesPerGroupValue || 1,
      direction: 'vertical',
      threshold: 5,
      navigation: {
        nextEl: this.hasThumbnailRightNavTarget ? this.thumbnailRightNavTarget : false,
        prevEl: this.hasThumbnailLeftNavTarget ? this.thumbnailLeftNavTarget : false
      },
      lazy: {
        enabled: true,
        loadOnTransitionStart: true,
        loadPrevNext: true
      },
      preloadImages: false
    });

    if (this.hasThumbsTranslateValue) {
      thumbsSlider.setTranslate(this.thumbsTranslateValue);
    }

    return {
      swiper: thumbsSlider
    };
  }

  updateThumbnailNavigation(): void {
    const swiper = this.slide?.thumbs?.swiper;

    if (!swiper || !this.hasThumbnailRightNavTarget || !this.hasThumbnailLeftNavTarget) {
      return;
    }

    const navigation = swiper.params?.navigation as
      | { nextEl?: HTMLElement; prevEl?: HTMLElement }
      | undefined;

    if (typeof navigation === 'object' && navigation !== null) {
      navigation.nextEl = this.thumbnailRightNavTarget;
      navigation.prevEl = this.thumbnailLeftNavTarget;
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }

  pagination() {
    return {
      el: this.hasPaginationTarget ? this.paginationTarget : null,
      clickable: this.hasPaginationTarget
    };
  }

  autoplay() {
    if (!this.slidesAutoPlayValue) {
      return false;
    }

    return {
      delay: 3500,
      disableOnInteraction: false,
      pauseOnMouseEnter: true
    };
  }

  navigation() {
    return {
      nextEl: this.hasRightNavTarget ? this.rightNavTarget : null,
      prevEl: this.hasLeftNavTarget ? this.leftNavTarget : null
    };
  }

  setDesktopLoopBreakpoint() {
    if (this.slidesLoopDesktopValue === 'false') {
      return false;
    }

    return this.slidesLoopValue;
  }

  breakpoints() {
    return {
      // when window width is >= 640px
      640: {
        slidesPerView: this.slidesPerViewSValue,
        slidesPerGroup: this.slidesPerGroupSValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 768px
      768: {
        slidesPerView: this.slidesPerViewMValue,
        slidesPerGroup: this.slidesPerGroupMValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 1024px
      1024: {
        slidesPerView: this.slidesPerViewLValue,
        slidesPerGroup: this.slidesPerGroupLValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 1280px
      1280: {
        slidesPerView: this.slidesPerViewXlValue,
        slidesPerGroup: this.slidesPerGroupXlValue,
        loop: this.setDesktopLoopBreakpoint()
      },
      // when window width is >= 1440px
      1440: {
        slidesPerView: this.slidesPerViewXxlValue,
        slidesPerGroup: this.slidesPerGroupXlValue,
        loop: this.setDesktopLoopBreakpoint()
      }
    };
  }

  adjustArrows = () => {
    const image = this.element.querySelector('.swiper-slide img');
    const leftArrow = this.hasLeftNavTarget ? this.leftNavTarget : null;
    const rightArrow = this.hasRightNavTarget ? this.rightNavTarget : null;

    if (image && leftArrow && rightArrow) {
      const imageRect = image.getBoundingClientRect();
      const carouselRect = this.element.getBoundingClientRect();

      const imageCenter = imageRect.top + imageRect.height / 2;
      const carouselTop = carouselRect.top;

      const offsetTop = imageCenter - carouselTop;

      this.element.style.setProperty('--pincer-swiper-navigation-top-offset', `${offsetTop}px`);
    }
  };

  alignArrows() {
    const image = this.element.querySelector('.swiper-slide img');

    if (image) {
      if (image.complete && image.naturalHeight !== 0) {
        this.adjustArrows();
      } else {
        image.addEventListener('load', this.adjustArrows);
      }
    }
  }

  disconnect() {
    // Clean up event listeners and observers
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  mount(slideTarget: HTMLElement) {
    if (!this.hasSlideTarget) {
      return null;
    }

    const initialSlide = window[this.slidesSyncCarouselIdValue]?.activeIndex ?? this.slidesStartAtValue;

    const config = {
      modules: [Autoplay, Navigation, Pagination, Thumbs],
      navigation: this.navigation(),
      autoHeight: this.slidesAutoHeightValue,
      slidesPerView: this.slidesPerViewXsValue,
      slidesPerGroup: 1,
      spaceBetween: this.slidesSpaceBetweenValue,
      pagination: this.pagination(),
      thumbs: this.thumbnails(),
      loop: this.slidesLoopValue,
      autoplay: this.autoplay(),
      observer: true,
      breakpoints: this.breakpoints(),
      initialSlide,
      threshold: 10,
      lazy: {
        enabled: true,
        loadOnTransitionStart: true,
        loadPrevNext: true
      },
      preloadImages: false
    };

    const swiper = new Swiper(slideTarget, config);
    this.slide = swiper;

    if (this.hasSlidesIdValue) {
      window[this.slidesIdValue] = swiper
    }

    if (this.slidesTrackActiveValue) {
      this.slide.on('slideChange', () => {
        this.slide.thumbs.swiper.slideTo(this.slide.activeIndex);
        if (this.hasSlidesSyncCarouselIdValue && window[this.slidesSyncCarouselIdValue]) {
          window[this.slidesSyncCarouselIdValue].slideTo(swiper.activeIndex, 0)
        }
        if (this.slidesLcIdValue) {
          window.dispatchEvent(
            new CustomEvent('vulcan:hook-relay:push-event-to', {
              detail: {
                selectorOrTarget: `#${this.slidesLcIdValue}`,
                event: 'update_live_active_slide',
                payload: {
                  slide: this.slide.activeIndex,
                  thumbs_translate: this.slide.thumbs.swiper.translate || 0
                }
              }
            })
          );
        }
      });

      this.slide.on('observerUpdate', () => {
        this.slide.update();
        this.slide.thumbs.swiper.setTranslate(this.slide.thumbs.swiper.translate);
        this.slide.thumbs.swiper.update();
        this.updateThumbnailNavigation();
      });
    }

    const verticallyAlignArrowsOnImageValue = this.containsClass(
      'slide',
      'valign-arrows-with-image'
    );

    if (verticallyAlignArrowsOnImageValue) {
      this.alignArrows();

      this.resizeObserver = new ResizeObserver(() => {
        this.adjustArrows();
      });
      this.resizeObserver.observe(slideTarget);
    }

    return this.slide;
  }

  initialize() {
    if (!this.hasSlideTarget) {
      return null;
    }
    const handleIntersection = (
      entries: IntersectionObserverEntry[],
      observer: IntersectionObserver
    ) => {
      entries.forEach((entry: IntersectionObserverEntry) => {
        if (entry.isIntersecting) {
          const target = entry.target as HTMLElement;
          if (!target.classList.contains('swiper-observer-initialized')) {
            this.mount(target);
            target.classList.add('swiper-observer-initialized');
          }

          observer.unobserve(target);
        }
      });
    };

    this.intersectionObserver = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '200px',
      threshold: 0
    });
    this.intersectionObserver.observe(this.slideTarget);
  }

  private containsClass(targetName: string, className: string): boolean {
    const targetElement = this[targetName + 'Target'];
    return targetElement.classList.contains(className);
  }
}
